import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import type { CostumeMascotte, StatoMascotte } from './mascotteScene';
import './mascotte.css';

export type { CostumeMascotte, StatoMascotte } from './mascotteScene';

/**
 * La mascotte dell'assistente (28/10/2026). Lorenzo: «carino tipo il robottino di
 * Claude animato in quella maniera tipo 8 bit ma nello stile di masamune, magari che
 * parte dal quadratino di masamune», poi «sempre in stile claude mascotte, sempre
 * pixel, non troppo dettagliata però un po' di più», «gli occhi devono rimanere quasi
 * sempre uguali a parte quando si socchiudono ma sempre rettangolari», la bocca no.
 *
 * È il quadratino del marchio (logos/masamune.svg, 99 × 88) a pixel, su una griglia
 * 20 × 20: corpo 16 × 14, due occhi rettangolari «bucati», due braccini, quattro
 * zampette. Gli oggetti (katana, onigiri, tazza, moneta, ombrello, costumi) compaiono
 * solo nella loro scena. Animazioni a scatti (steps), come a 8 bit: mascotte.css.
 * Colori dal tema: al buio si rovescia da sola.
 *
 * Chi sceglie la scena: features/assistente/mascotteVita.ts. Tutte le scene una
 * accanto all'altra: /mascotte.
 */

interface Props {
  stato?: StatoMascotte;
  /** Lato in pixel: multipli di 20 tengono i pixel netti (20, 40, 60, 80). */
  size?: number;
  className?: string;
  title?: string;
  /** Gli occhi seguono il puntatore quando è ferma (la mascotte in alto a destra). */
  segui?: boolean;
  /** Dove guardano gli occhi, se lo decide chi la usa (-1, 0, 1 per asse): vince su `segui`. */
  occhi?: { x: number; y?: number } | null;
  /** Il costume della festa, sopra qualunque scena. */
  costume?: CostumeMascotte | null;
  /** Per «spinge»: quanti pixel della griglia deve camminare a sinistra per arrivare al bottone. */
  spinta?: number;
}

type Colore = 'c' | 'o' | 'l';
const CLASSE: Record<Colore, string> = { c: 'corpo', o: 'occhio', l: 'lama' };

/** Un rettangolo di pixel della griglia. */
const P = ({ x, y, w = 1, h = 1, c, className }: { x: number; y: number; w?: number; h?: number; c: Colore; className?: string }) => (
  <rect x={x} y={y} width={w} height={h} className={cn(CLASSE[c], className)} />
);

/** Pixel singoli: [[x, y], …]. */
const Pixel = ({ punti, c }: { punti: Array<[number, number]>; c: Colore }) => (
  <>
    {punti.map(([x, y]) => (
      <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} className={CLASSE[c]} />
    ))}
  </>
);

/** Gli occhi verso il puntatore: -1, 0 o 1 pixel per asse, solo se si è mosso da poco. */
function useSguardo(attivo: boolean, ref: React.RefObject<SVGSVGElement>) {
  const [dir, setDir] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!attivo) { setDir(null); return; }
    let raf = 0;
    let fermo = 0;
    const onMove = (e: PointerEvent) => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        const soglia = r.width * 0.6;
        setDir({ x: dx > soglia ? 1 : dx < -soglia ? -1 : 0, y: dy > soglia ? 1 : dy < -soglia ? -1 : 0 });
        window.clearTimeout(fermo);
        // Puntatore fermo da un po': torna a guardarsi intorno da sola.
        fermo = window.setTimeout(() => setDir(null), 2500);
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.cancelAnimationFrame(raf);
      window.clearTimeout(fermo);
    };
  }, [attivo, ref]);
  return dir;
}

export function Mascotte({ stato = 'pronto', size = 40, className, title = 'MUNE', segui = false, occhi, costume, spinta = 5 }: Props) {
  const ref = useRef<SVGSVGElement>(null);
  const puntatore = useSguardo(segui && !occhi && stato === 'pronto', ref);
  const dir = occhi ? { x: occhi.x, y: occhi.y ?? 0 } : puntatore;
  // Ogni mascotte sbatte le palpebre e si guarda intorno quando vuole lei: con lo stesso
  // ritmo per tutte sembravano un coro (Lorenzo, 28/10).
  const [ritardo] = useState(() => `${-(Math.random() * 7).toFixed(2)}s`);

  return (
    <svg
      ref={ref}
      viewBox="0 0 20 20"
      width={size}
      height={size}
      className={cn('msc', className)}
      data-stato={stato}
      data-costume={costume ?? undefined}
      data-segue={dir ? 'true' : undefined}
      style={{
        ['--msc-ritardo' as string]: ritardo,
        ['--msc-spinta' as string]: String(spinta),
        ...(dir ? { ['--msc-px' as string]: `${dir.x}px`, ['--msc-py' as string]: `${dir.y}px` } : {}),
      } as React.CSSProperties}
      role="img"
      aria-label={title}
    >
      <g className="msc-tutto">
        {/* Quattro zampette da 2 pixel, a coppie alterne quando cammina o balla. */}
        <g className="msc-zampe msc-zampe-a">
          <P x={3} y={18} w={2} h={2} c="c" /><P x={12} y={18} w={2} h={2} c="c" />
        </g>
        <g className="msc-zampe msc-zampe-b">
          <P x={6} y={18} w={2} h={2} c="c" /><P x={15} y={18} w={2} h={2} c="c" />
        </g>

        <g className="msc-busto">
          {/* Il corpo: il quadratino del marchio, con due braccini. */}
          <P x={2} y={4} w={16} h={14} c="c" className="msc-corpo" />
          <P x={1} y={11} h={2} c="c" className="msc-braccio-s" />
          <P x={18} y={11} h={2} c="c" className="msc-braccio-d" />
          {/* Il braccino che indica a sinistra, verso «Da fare». */}
          <P x={-1} y={11} w={3} c="c" className="msc-indica" />

          {/* Gli occhi: sempre due rettangoli. Si muovono, sbattono e si socchiudono, e
              basta (Lorenzo: «devono rimanere quasi sempre uguali a parte quando si
              socchiudono ma sempre rettangolari»). Niente bocca. */}
          <g className="msc-sguardo">
            <g className="msc-occhi">
              <P x={6} y={8} w={2} h={3} c="o" />
              <P x={12} y={8} w={2} h={3} c="o" />
            </g>
          </g>

          {/* L'onigiri del pranzo, tenuto davanti: riso e alga. */}
          <g className="msc-onigiri">
            <P x={9} y={12} w={2} c="o" />
            <P x={8} y={13} w={4} c="o" />
            <P x={8} y={14} w={4} c="l" />
          </g>
          {/* La tazza di tè della mattina, col vapore che sale a scatti. */}
          <g className="msc-tazza">
            <P x={8} y={13} w={3} c="l" />
            <P x={8} y={14} w={3} h={2} c="o" />
            <P x={11} y={14} c="o" />
            <g className="msc-vapore msc-vapore-a"><Pixel punti={[[9, 12], [10, 11]]} c="o" /></g>
            <g className="msc-vapore msc-vapore-b"><Pixel punti={[[10, 12], [9, 11]]} c="o" /></g>
          </g>

          {/* I costumi da mettere in testa (data-costume), dentro il busto: seguono la testa in
              ogni scena. Fuori, la zucca restava ferma mentre il corpo saltava, camminava o
              faceva la capriola (Lorenzo, 30/10/2026: «la zucca non gli rimane in testa»). */}
          <g className="msc-costume msc-costume-zucca">
            <P x={7} y={1} w={6} c="l" />
            <P x={6} y={2} w={8} h={2} c="l" />
            <P x={10} y={0} c="c" />
            <Pixel punti={[[8, 2], [11, 2]]} c="o" />
          </g>
          <g className="msc-costume msc-costume-natale">
            <P x={4} y={4} w={12} c="o" />
            <P x={6} y={3} w={8} c="l" />
            <P x={8} y={2} w={5} c="l" />
            <P x={10} y={1} w={3} c="l" />
            <P x={13} y={0} w={2} h={2} c="c" />
          </g>
        </g>

        {/* La katana, solo in certe scene: alzata col braccio destro, con un riflesso. */}
        <g className="msc-katana">
          <P x={18} y={9} h={2} c="c" />
          <P x={19} y={8} h={2} c="c" />
          <P x={18} y={7} w={2} c="c" />
          <P x={19} y={0} h={7} c="l" />
          <P x={19} y={5} c="o" className="msc-riflesso" />
        </g>
        {/* Il fendente: una scia che attraversa tutto in diagonale. */}
        <g className="msc-scia">
          <Pixel punti={[[19, 1], [18, 2], [17, 3], [16, 4], [15, 5], [14, 6], [13, 7], [12, 8], [11, 9], [10, 10], [9, 11], [8, 12], [7, 13], [6, 14], [5, 15], [4, 16], [3, 17], [2, 18], [1, 19]]} c="l" />
        </g>

        {/* L'ombrello, quando la rete o un servizio non rispondono: piove intorno. */}
        <g className="msc-ombrello">
          <P x={7} y={0} w={6} c="l" />
          <P x={5} y={1} w={10} c="l" />
          <Pixel punti={[[5, 2], [14, 2]]} c="l" />
          <P x={10} y={2} h={2} c="c" />
          <g className="msc-pioggia msc-pioggia-a"><Pixel punti={[[1, 1], [18, 0], [2, 4], [17, 3]]} c="l" /></g>
          <g className="msc-pioggia msc-pioggia-b"><Pixel punti={[[1, 3], [18, 2], [2, 6], [17, 5]]} c="l" /></g>
        </g>
      </g>

      {/* Fuori dal corpo: la moneta che cade, i puntini (pensa), la z (dorme), le stelline (stordito). */}
      <g className="msc-moneta">
        <P x={9} y={0} w={2} h={2} c="l" /><P x={9} y={0} c="o" />
      </g>
      {/* Quadrati di 2 × 2: alla mascotte piccola nella chat (20px) un pixel solo non si vedeva. */}
      <g className="msc-puntini">
        <P x={5} y={0} w={2} h={2} c="c" className="msc-p1" />
        <P x={9} y={0} w={2} h={2} c="c" className="msc-p2" />
        <P x={13} y={0} w={2} h={2} c="c" className="msc-p3" />
      </g>
      <g className="msc-zeta">
        <P x={15} y={0} w={3} c="c" /><P x={16} y={1} c="c" /><P x={15} y={2} w={3} c="c" />
      </g>
      <g className="msc-stelle">
        <P x={4} y={2} c="c" className="msc-s1" /><P x={10} y={0} c="c" className="msc-s2" /><P x={16} y={2} c="c" className="msc-s3" />
      </g>

      {/* I fuochi di Capodanno stanno intorno alla mascotte, non in testa: restano fuori dal corpo. */}
      <g className="msc-costume msc-costume-capodanno">
        <g className="msc-fuoco msc-fuoco-a">
          <P x={1} y={1} c="l" />
          <Pixel punti={[[1, -1], [-1, 1], [3, 1], [1, 3]]} c="c" />
        </g>
        <g className="msc-fuoco msc-fuoco-b">
          <P x={18} y={0} c="l" />
          <Pixel punti={[[17, -1], [19, -1], [17, 1], [19, 1]]} c="c" />
        </g>
      </g>
    </svg>
  );
}
